/**
 * Base64 without platform helpers.
 *
 * `btoa` is deprecated in Node and `Uint8Array.toBase64` is not available in
 * every target we ship to, so the codec carries its own implementation. Both
 * runtimes must produce byte-identical output or a blob written on one device
 * would fail to decode on another.
 */

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

const LOOKUP: Int8Array = (() => {
  const table = new Int8Array(256).fill(-1);
  for (let i = 0; i < ALPHABET.length; i++) table[ALPHABET.charCodeAt(i)] = i;
  return table;
})();

export function bytesToBase64(bytes: Uint8Array<ArrayBufferLike>): string {
  let out = "";
  let i = 0;

  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8) | bytes[i + 2]!;
    out +=
      ALPHABET[(n >>> 18) & 63]! +
      ALPHABET[(n >>> 12) & 63]! +
      ALPHABET[(n >>> 6) & 63]! +
      ALPHABET[n & 63]!;
  }

  const remaining = bytes.length - i;
  if (remaining === 1) {
    const n = bytes[i]! << 16;
    out += ALPHABET[(n >>> 18) & 63]! + ALPHABET[(n >>> 12) & 63]! + "==";
  } else if (remaining === 2) {
    const n = (bytes[i]! << 16) | (bytes[i + 1]! << 8);
    out +=
      ALPHABET[(n >>> 18) & 63]! +
      ALPHABET[(n >>> 12) & 63]! +
      ALPHABET[(n >>> 6) & 63]! +
      "=";
  }

  return out;
}

export function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  let end = value.length;
  while (end > 0 && value[end - 1] === "=") end--;

  // Exact, so the result never needs a subarray: every 4 input characters carry
  // 3 bytes, and a trailing group of 2 or 3 carries 1 or 2. WebCrypto rejects a
  // view backed by a larger buffer, so the size has to be right the first time.
  const out = new Uint8Array(Math.floor((end * 3) / 4));
  let outIndex = 0;
  let buffer = 0;
  let bits = 0;

  for (let i = 0; i < end; i++) {
    const sextet = LOOKUP[value.charCodeAt(i)]!;
    if (sextet < 0) throw new Error("base64: invalid character at index " + i);

    buffer = (buffer << 6) | sextet;
    bits += 6;

    if (bits >= 8) {
      bits -= 8;
      out[outIndex++] = (buffer >>> bits) & 0xff;
    }
  }

  if (outIndex !== out.length) {
    throw new Error("base64: truncated input");
  }

  return out;
}
